import type { IconName } from '@customerio/pluma-icons';
import { anyMessageValue, messageChannelOf } from './message-channels';
import { campaignTypeIcon, type PickerMessage } from './message-picker-data';

/**
 * Ordering and grouping for the message picker's dropdown, over the
 * segmentable message list. The ordered list is cached per source array and
 * spec, because every picker on the page derives from the same workspace
 * message set and the transform is O(n log n) over potentially thousands of
 * messages; the cache drops whole when the source list is replaced.
 */

export type DropdownGroup = {
  isGroup: true;
  isTopLevelGroup: boolean;
  showBottomBorder?: boolean;
  /**
   * Identity for merging paged results: the parent's id for sub-groups, the
   * group type for top-level sections. Display labels can collide (two
   * campaigns may share a name), so merging must key on this instead.
   */
  groupId: string;
  groupName: string;
  groupType: string;
  icon?: IconName;
  options: (PickerMessage | DropdownGroup)[];
};

const GROUP_SORT_ORDER = ['campaign', 'newsletter', 'broadcast', 'in_app_broadcast', 'transactional message'];

// `localeCompare` with an options object builds a fresh `Intl.Collator` per
// call, and the comparator below runs O(n log n) times over the workspace's
// messages. Sharing one collator is ~50x on the sort itself.
const MESSAGE_COLLATOR = new Intl.Collator(undefined, { sensitivity: 'accent' });

// Mirrors `a?.localeCompare(b ?? '')`: a nullish `a` yields undefined, so the
// caller falls through to the next sort key instead of ordering on this one.
function compareOptional(a: string | null | undefined, b: string | null | undefined): number | undefined {
  if (a == null) {
    return undefined;
  }
  return MESSAGE_COLLATOR.compare(a, b ?? '');
}

function channelShortName(channel: string): string {
  return messageChannelOf(channel)?.shortName ?? channel;
}

/**
 * The stored placeholder for a message that no longer exists. Kept out of the
 * cached core because it is the only part of the ordered list that depends on
 * the individual condition. Returns a new array rather than unshifting, so a
 * cached list is never mutated.
 */
export function withDeletedPlaceholder(
  ordered: PickerMessage[],
  currentId: string | null | undefined,
  isDeleted: boolean,
  channel: string,
): PickerMessage[] {
  if (currentId == null || !isDeleted) {
    return ordered;
  }
  const shortName = channelShortName(channel);
  const subject = `${shortName.charAt(0).toUpperCase()}${shortName.slice(1)} deleted`;
  return [{ type: channel, subject, isDeleted: true, output_id: currentId }, ...ordered];
}

export type OrderedMessagesSpec = {
  /** Message types this picker offers, e.g. `['twilio', 'sms']`. */
  types: string[];
  /** Channel the picker itself is for, which drives the wildcard copy. */
  type: string;
  includeVariations: boolean;
  includeLanguageBranches: boolean;
  allowWildcardSelection: boolean;
  allowInParentWildcardSelection: boolean;
  // Distinct from `false`: only an explicit `false` drops in-app broadcast rows.
  allowInAppBroadcastSelection: boolean | undefined;
};

export type GroupingLabels = {
  /** "campaign" per the workspace's product taxonomy. */
  campaignNoun: string;
  /** "Campaigns" per the taxonomy — the top-level campaign group heading. */
  campaignsPlural: string;
  /** "Newsletters" per the taxonomy. */
  newslettersPlural: string;
};

/**
 * Campaigns that mix legacy `twilio` and custom `sms` actions emit one
 * per-campaign wildcard per message type — the two address different backend
 * bitmaps — but both channels' catalog short name is "SMS", so the pair would
 * read identically. Returns the campaign ids where the legacy entry needs a
 * "(Twilio)" qualifier; unmixed campaigns keep the plain copy.
 */
function findMixedSmsCampaigns(messages: PickerMessage[], types: string[]): Set<string> {
  const mixed = new Set<string>();
  if (!types.includes('twilio') || !types.includes('sms')) {
    return mixed;
  }
  const firstSmsTypeByCampaign = new Map<string, string>();
  for (const message of messages) {
    const campaignId = smsCampaignIdOf(message);
    if (campaignId == null) {
      continue;
    }
    const seenType = firstSmsTypeByCampaign.get(campaignId) ?? message.type;
    firstSmsTypeByCampaign.set(campaignId, seenType);
    if (seenType !== message.type) {
      mixed.add(campaignId);
    }
  }
  return mixed;
}

/** The campaign id of a campaign/broadcast-parented twilio or sms message, or null for anything else. */
function smsCampaignIdOf(message: PickerMessage): string | null {
  if (message.type !== 'twilio' && message.type !== 'sms') {
    return null;
  }
  const parentType = message.parent?.type;
  if (parentType !== 'campaign' && parentType !== 'broadcast') {
    return null;
  }
  return message.campaign_id ?? null;
}

// fallow-ignore-next-line complexity -- the legacy picker's ordering rules; the comparator is the behavior
function orderMessagesCore(messages: PickerMessage[], spec: OrderedMessagesSpec, labels: GroupingLabels) {
  const shortName = channelShortName(spec.type);
  const mixedSmsCampaigns = findMixedSmsCampaigns(messages, spec.types);
  const orderedMessages: PickerMessage[] = [];

  if (spec.type === 'email' || spec.allowWildcardSelection) {
    let parentName = `in any ${labels.campaignNoun}, broadcast, or transactional message`;
    if (spec.allowInAppBroadcastSelection === true && spec.type === 'in_app') {
      parentName = `in any ${labels.campaignNoun}, broadcast, anonymous or transactional message`;
    }
    orderedMessages.push({
      type: spec.type,
      subject: `any ${shortName}`,
      searchLabel: `any ${shortName}`,
      isAny: true,
      isTopLevelWildcard: true,
      hideIcon: true,
      output_id: anyMessageValue(spec.type),
      parent: { id: '', type: 'any', name: parentName },
    });
  }

  let items: PickerMessage[] = [];
  const wildcards = new Set<string>();

  for (const item of messages) {
    if (item.parent?.type === 'campaign' || item.parent?.type === 'broadcast') {
      // Key the wildcard by channel type as well as campaign, so a campaign
      // that mixes channels (e.g. a legacy `twilio` and a custom `sms` action)
      // emits one "any … in campaign" wildcard per channel instead of a single
      // order-dependent one that would miss the other channel's messages.
      const wildcardType = item.type;
      const id = `c_${item.campaign_id}_${wildcardType}`;
      if (spec.allowInParentWildcardSelection && !wildcards.has(id)) {
        // The label derives from the entry's own type, not the picker's, and
        // the legacy half of a mixed twilio/sms campaign gets a qualifier —
        // the two types share the "SMS" short name.
        const qualifier =
          wildcardType === 'twilio' && item.campaign_id != null && mixedSmsCampaigns.has(item.campaign_id)
            ? ' (Twilio)'
            : '';
        const wildcardName = `any ${channelShortName(wildcardType)}${qualifier}`;
        items.push({
          type: spec.type,
          subject: wildcardName,
          searchLabel: `${wildcardName} in ${item.parent.name}`,
          isAny: true,
          isAnyInParent: true,
          isGroupWildcard: true,
          hideIcon: true,
          output_id: `any_${wildcardType}-campaign_${item.campaign_id}`,
          parent: { ...item.parent },
        });
        wildcards.add(id);
      }
    }

    if (item.parent?.type === 'in_app_broadcast' && spec.allowInAppBroadcastSelection === false) {
      continue;
    }
    items.push(item);
  }

  // fallow-ignore-next-line complexity -- the legacy comparator's tie-break chain, kept verbatim
  items = items.sort((a, b) => {
    if (a.parent != null && b.parent != null && a.parent.type === b.parent.type) {
      if (a.parent.id === b.parent.id) {
        if (a.isGroupWildcard === true && b.isGroupWildcard !== true) {
          return -1;
        }
        if (a.isGroupWildcard !== true && b.isGroupWildcard === true) {
          return 1;
        }
        if (a.parent.type === 'newsletter' && a.can_segment_on_variation === true) {
          // Sorts the "All variations" item before the variations, and then
          // the variations in A, B, C order.
          if (a.is_segmentable_variation === true && b.is_segmentable_variation !== true) {
            return 1;
          }
          if (a.is_segmentable_variation !== true && b.is_segmentable_variation === true) {
            return -1;
          }
          return (a.variation ?? 0) - (b.variation ?? 0);
        }
        if (a.sort_value === b.sort_value) {
          if (a.isLanguageBranch === true && b.isLanguageBranch !== true) {
            return -1;
          }
          if (a.isLanguageBranch !== true && b.isLanguageBranch === true) {
            return 1;
          }
        }
        const subjectSortValue = compareOptional(a.subject, b.subject);
        if (subjectSortValue != null && subjectSortValue !== 0) {
          return subjectSortValue;
        }
        return compareOptional(a.language, b.language) ?? 0;
      }
      return MESSAGE_COLLATOR.compare(a.parent.name, b.parent.name);
    }

    if (a.parent != null && b.parent != null) {
      return GROUP_SORT_ORDER.indexOf(a.parent.type) - GROUP_SORT_ORDER.indexOf(b.parent.type);
    }
    return 0;
  });

  // Not `push(...items)`: spreading into a call puts every element on the JS
  // call stack, which overflows on workspaces with ~100k+ messages.
  for (const item of items) {
    orderedMessages.push(item);
  }
  return orderedMessages;
}

const orderedMessagesCache = new WeakMap<PickerMessage[], Map<string, PickerMessage[]>>();

function specKey(spec: OrderedMessagesSpec): string {
  return [
    spec.types.join(','),
    spec.type,
    spec.includeVariations,
    spec.includeLanguageBranches,
    spec.allowWildcardSelection,
    spec.allowInParentWildcardSelection,
    spec.allowInAppBroadcastSelection,
  ].join('|');
}

function filterSegmentable(segmentable: PickerMessage[], spec: OrderedMessagesSpec): PickerMessage[] {
  return segmentable.filter((message) => {
    if (!spec.types.includes(message.type)) {
      return false;
    }
    if (!spec.includeVariations && message.is_segmentable_variation === true) {
      return false;
    }
    return spec.includeLanguageBranches || message.isLanguageBranch !== true;
  });
}

/**
 * The ordered dropdown list for one picker spec, shared across every picker
 * asking for the same thing. The result is cached and must not be mutated;
 * per-condition additions go through {@link withDeletedPlaceholder}.
 */
export function orderedMessagesFor(
  segmentable: PickerMessage[],
  spec: OrderedMessagesSpec,
  labels: GroupingLabels,
): PickerMessage[] {
  let byKey = orderedMessagesCache.get(segmentable);
  if (byKey == null) {
    byKey = new Map();
    orderedMessagesCache.set(segmentable, byKey);
  }

  const key = specKey(spec);
  const cached = byKey.get(key);
  if (cached != null) {
    return cached;
  }

  const ordered = orderMessagesCore(filterSegmentable(segmentable, spec), spec, labels);
  byKey.set(key, ordered);
  return ordered;
}

type MutableGroup = DropdownGroup & { groups: Map<string, DropdownGroup> };

function topLevelGroup(groupName: string, groupType: string, showBottomBorder = false): MutableGroup {
  return {
    isGroup: true,
    isTopLevelGroup: true,
    showBottomBorder: showBottomBorder || undefined,
    groupId: groupType,
    groupName,
    groupType,
    options: [],
    groups: new Map(),
  };
}

function subGroupFor(parentGroup: MutableGroup, id: string, groupName: string, groupType: string, icon: IconName) {
  let group = parentGroup.groups.get(id);
  if (group == null) {
    group = { isGroup: true, isTopLevelGroup: false, groupId: id, groupName, groupType, icon, options: [] };
    parentGroup.groups.set(id, group);
  }
  return group;
}

/**
 * Groups an ordered page of messages into the dropdown's nested structure:
 * Campaigns → per-campaign groups, Newsletters → per-newsletter groups, then
 * flat Transactional and Anonymous sections. The deleted placeholder and the
 * top-level wildcard stay ungrouped at the top.
 */
// fallow-ignore-next-line complexity -- the legacy grouping's per-parent-type rules, kept in one auditable pass
export function groupMessages(messages: PickerMessage[], labels: GroupingLabels): (PickerMessage | DropdownGroup)[] {
  const ungrouped: PickerMessage[] = [];
  const groups = new Map<string, MutableGroup>();

  for (const item of messages) {
    if (item.isDeleted === true || item.isTopLevelWildcard === true) {
      ungrouped.push(item);
      continue;
    }

    const itemClone: PickerMessage = {
      ...item,
      showLeftOffset: !(item.variation != null && item.is_segmentable_variation === true),
      hideIcon: true,
      hideParent: item.isGroupWildcard !== true,
    };

    const parent = item.parent;
    if (parent?.type === 'newsletter') {
      const top = groups.get('newsletter') ?? topLevelGroup(labels.newslettersPlural, 'newsletter');
      groups.set('newsletter', top);
      subGroupFor(top, `n_${parent.id}`, parent.name, 'newsletter', 'newsletter').options.push(itemClone);
    } else if (parent?.type === 'campaign' || parent?.type === 'broadcast') {
      const heading = parent.type === 'campaign' ? labels.campaignsPlural : 'API-triggered broadcasts';
      const top = groups.get(parent.type) ?? topLevelGroup(heading, parent.type);
      groups.set(parent.type, top);
      subGroupFor(top, `c_${parent.id}`, parent.name, parent.type, campaignTypeIcon(parent.campaignType)).options.push(
        itemClone,
      );
    } else if (parent?.type === 'transactional message') {
      const top = groups.get('transactional message') ?? topLevelGroup('Transactional messages', parent.type, true);
      groups.set('transactional message', top);
      itemClone.hideParent = true;
      top.options.push(itemClone);
    } else if (parent?.type === 'in_app_broadcast') {
      const top = groups.get('in_app_broadcast') ?? topLevelGroup('Anonymous messages', parent.type, true);
      groups.set('in_app_broadcast', top);
      itemClone.hideParent = true;
      itemClone.subject = parent.name;
      top.options.push(itemClone);
    } else {
      // A message whose parent did not resolve has no section; keep it
      // visible as an ungrouped entry — dropping it would leave "load more"
      // counting entries that never render.
      ungrouped.push(itemClone);
    }
  }

  const grouped: (PickerMessage | DropdownGroup)[] = [...ungrouped];
  for (const group of groups.values()) {
    const { groups: subGroupsById, ...rest } = group;
    if (subGroupsById.size > 0) {
      const subGroups = [...subGroupsById.values()].sort((a, b) => compareLowercase(a.groupName, b.groupName));
      grouped.push({ ...rest, options: subGroups });
    } else {
      grouped.push(rest);
    }
  }

  // The picker's top order: the wildcard, then any deleted placeholder, then
  // the groups in their fixed section order.
  return grouped.sort((a, b) => {
    const rankDiff = entryRank(a) - entryRank(b);
    if (rankDiff !== 0) {
      return rankDiff;
    }
    if ('isGroup' in a && 'isGroup' in b) {
      return GROUP_SORT_ORDER.indexOf(a.groupType) - GROUP_SORT_ORDER.indexOf(b.groupType);
    }
    return 0;
  });
}

function entryRank(entry: PickerMessage | DropdownGroup): number {
  if ('isGroup' in entry) {
    return 2;
  }
  if (entry.isTopLevelWildcard === true) {
    return 0;
  }
  if (entry.isDeleted === true) {
    return 1;
  }
  return 2;
}

function compareLowercase(a: string, b: string): number {
  const left = a.toLowerCase();
  const right = b.toLowerCase();
  if (left < right) return -1;
  if (left > right) return 1;
  return 0;
}
